import {
  ACQUISITION_KEYS, clickKeyBody, clickSignals, gaIdentifiersFromCookies, hasAcquisition, hasClickId, newClickKey,
  normalizeAcquisition, persistableAcquisition, registrationEventId as buildRegistrationEventId, pixelIdentifiersFromCookies, shouldReplaceAcquisition,
} from '@baba/attribution'
import {
  consentGate, identifyAdUser, loadMetaPixel, loadTikTokPixel, newEventId, trackAdEvent, whenConsented, type AdEventName,
} from '@baba/attribution/ad-pixels'
import { consentForApi } from '@baba/attribution/consent'

// Re-exported so lib/baba-api.ts has one analytics import (its tests stub this module).
export { checkoutSessionId, checkoutSignalsBody } from '@baba/attribution'
import { readJwtClaims } from './baba-auth'
import { getGoogleAdsConfig } from './google-ads-env'
import { phCapture, phCaptureDurable } from './posthog'
import { scrubSecretPath } from './analyticsUrl'

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined
const TIKTOK_PIXEL_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined
const { signupSendTo: GOOGLE_ADS_SIGNUP_SEND_TO } = getGoogleAdsConfig()

const CLICK_COOKIE = 'baba_click'
/** Everything the shared record can hold; pixel ids and the click key come from cookies and the landing site, not the address bar. */
const CLICK_ATTR_KEYS = ACQUISITION_KEYS.filter(key => key !== 'fbp' && key !== 'fbc' && key !== 'ttp')

type PaidClickAttribution = Partial<Record<(typeof ACQUISITION_KEYS)[number], string>>

const adStorageGranted = () => consentGate()?.allowed('ad_storage') === true
/** Held for this page when ad_storage is not (yet) granted, so a later accept loses nothing. */
let memoryAttribution: PaidClickAttribution | null = null

function readClickCookie(): PaidClickAttribution | null {
  if (typeof document === 'undefined') return null
  try {
    const match = document.cookie.split('; ').find(row => row.startsWith(`${CLICK_COOKIE}=`))
    return match ? normalizeAcquisition(JSON.parse(decodeURIComponent(match.slice(CLICK_COOKIE.length + 1)))) : null
  } catch { return null }
}

function writeClickCookie(full: PaidClickAttribution): void {
  if (typeof document === 'undefined') return
  const attr = persistableAcquisition(full as Record<string, string>, adStorageGranted())
  if (!attr) return
  const domain = /(^|\.)itsbaba\.com$/.test(window.location.hostname) ? '; Domain=.itsbaba.com' : ''
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  try {
    document.cookie = `${CLICK_COOKIE}=${encodeURIComponent(JSON.stringify(attr))}; Max-Age=${90 * 24 * 60 * 60}; Path=/${domain}; SameSite=Lax${secure}`
  } catch { /* Attribution must not block checkout. */ }
}

function currentUrlAttribution(): PaidClickAttribution {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const attr: PaidClickAttribution = {}
  for (const key of CLICK_ATTR_KEYS) {
    const value = params.get(key)
    if (value) attr[key] = value.slice(0, 200)
  }
  if (!hasAcquisition(attr)) return {}
  if (!attr.click_ts) attr.click_ts = new Date().toISOString()
  if (!attr.landing_path) attr.landing_path = window.location.pathname
  if (!attr.referrer && document.referrer) attr.referrer = document.referrer

  return normalizeAcquisition(attr) ?? {}
}

/** The newer of the shared cookie and this page's memory; on a tie memory, which is the fuller copy. */
function pickStored(cookie: PaidClickAttribution | null, memory: PaidClickAttribution | null): PaidClickAttribution {
  if (!cookie) return memory ?? {}
  if (!memory) return cookie
  const at = (value: PaidClickAttribution) => Date.parse(value.click_ts || '') || 0
  return at(cookie) > at(memory) ? cookie : memory
}

export function capturePaidClickAttribution(): PaidClickAttribution {
  const fromUrl = currentUrlAttribution()
  const fromCookie = pickStored(readClickCookie(), memoryAttribution)
  // A new acquisition replaces the complete campaign, including old click IDs, unless it is only an
  // owned-channel UTM (the extension's welcome page) arriving after a stored paid click.
  const replace = shouldReplaceAcquisition(fromCookie, fromUrl)
  let merged: PaidClickAttribution = replace ? fromUrl : fromCookie
  // One key per acquisition: the join between this visit and the app install it may produce.
  const keyed = Object.keys(merged).length > 0 && !merged.click_key
  if (keyed) merged = { ...merged, click_key: newClickKey() }
  if (replace || keyed) {
    memoryAttribution = merged
    writeClickCookie(merged)
  } else if (Object.keys(merged).length) {
    memoryAttribution = merged
  }
  return merged
}

export function getPaidClickAttribution(): PaidClickAttribution {
  return capturePaidClickAttribution()
}

/**
 * The click ids, UTMs and click key to forward with a magic link, a checkout or POST /click-key.
 * Pixel ids ride along only while ad_storage is granted.
 */
export function getClickSignals(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  return clickSignals(getPaidClickAttribution() as Record<string, string>, document.cookie, { includePixelIds: adStorageGranted() })
}

export function getConsentForApi() {
  return consentForApi(consentGate()?.get() ?? null)
}

const PIXEL_ID_KEYS = ['fbp', 'fbc', 'ttp'] as const

/** Top up the stored record with the pixels' own ids once they exist; strip them when ad_storage is withdrawn. */
export function syncPixelIds(): void {
  if (typeof window === 'undefined') return
  const current = getPaidClickAttribution()
  if (!Object.keys(current).length) return
  const next: PaidClickAttribution = { ...current }
  for (const key of PIXEL_ID_KEYS) delete next[key]
  if (adStorageGranted()) Object.assign(next, pixelIdentifiersFromCookies(document.cookie, current as Record<string, string>))
  if (PIXEL_ID_KEYS.every(key => next[key] === current[key])) return
  memoryAttribution = next
  writeClickCookie(next)
}

let clickKeySent = ''

/** Tell the API which click ids belong to this click key. Idempotent for an unchanged record. */
export function registerClickKey(): void {
  // Click ids reach our API only from a visitor who allowed ad_storage.
  if (!adStorageGranted()) return
  const body = clickKeyBody(getClickSignals())
  if (!body) return
  const signature = JSON.stringify(body)
  if (signature === clickKeySent) return
  clickKeySent = signature
  void import('./baba-api').then(api => api.postClickKey(body)).catch(() => { clickKeySent = '' })
}

/**
 * Before ad_storage is granted a visit lives in memory only. Once it is granted the full record
 * is written, the pixels' ids join it when they appear, and a paid click is registered with the
 * API. Withdrawing it strips the identifiers from the cookie again.
 */
function persistAcquisitionOnConsent(): void {
  const gate = consentGate()
  if (!gate) return
  gate.when('ad_storage', () => {
    const record = getPaidClickAttribution()
    if (Object.keys(record).length) writeClickCookie(record)
    window.setTimeout(() => { syncPixelIds(); if (hasClickId(getPaidClickAttribution())) registerClickKey() }, 2500)
  })
  gate.subscribe(() => {
    if (adStorageGranted()) return
    const record = getPaidClickAttribution()
    if (Object.keys(record).length) writeClickCookie(record)
  })
}

// Capture once at app startup too, so a paid click survives route changes before auth/checkout.
if (typeof window !== 'undefined') {
  capturePaidClickAttribution()
  persistAcquisitionOnConsent()
}

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
  }
}

// The pixels read the address bar themselves, on load and on every automatic
// event, so no scrubbing reaches them (lib/analyticsUrl.ts). A page whose path
// is a credential (a shared chat link) does not load them at all. Both wait for
// the consent gate (index.html) to grant ad_storage: in the EEA, the UK and
// Switzerland that is after the visitor accepts.
if (typeof window !== 'undefined' && scrubSecretPath(window.location.pathname) === window.location.pathname) {
  const start = () => {
    whenConsented('ad_storage', () => {
      if (PIXEL_ID) loadMetaPixel(PIXEL_ID)
      if (TIKTOK_PIXEL_ID) loadTikTokPixel(TIKTOK_PIXEL_ID)
    })
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
  else start()
}

function ga(eventName: string, params?: Record<string, unknown>) {
  try {
    window.gtag?.('event', eventName, params)
  } catch {}
}

/** Identifiers use the same stream and formats as the server and landing site. */
export function getGaClientId(): string | null {
  try { return gaIdentifiersFromCookies(document.cookie).ga_client_id ?? null } catch { return null }
}

export function getGaSessionId(): string | null {
  try { return gaIdentifiersFromCookies(document.cookie).ga_session_id ?? null } catch { return null }
}

/** PostHog, queued until the client has loaded (see lib/posthog.ts). */
function ph(eventName: string, props?: Record<string, unknown>) {
  phCapture(eventName, props)
}

/**
 * Standard event to Meta and TikTok with one event id. Events with a server twin pass the
 * server's id (`reg_<userId>`, the Stripe Checkout Session id); browser-only events get a UUID.
 */
function adEvent(name: AdEventName, params: Record<string, unknown> | undefined, eventId?: string) {
  trackAdEvent(name, params, eventId ?? newEventId())
}

function fbCustom(eventName: string, params?: Record<string, unknown>) {
  try {
    window.fbq?.('trackCustom', eventName, params)
  } catch {}
}

export function trackMagicLinkRequested(props: { intent: string }) {
  ga('magic_link_requested', props)
  ph('magic_link_requested', props)
  fbCustom('MagicLinkRequested', props)
}

/** `reg_<userId>`: the sign-up response's `eventId`, or rebuilt from the session JWT for the paths that never see the response. */
export function registrationEventId(serverEventId?: string | null, sessionToken?: string | null): string | undefined {
  return buildRegistrationEventId(serverEventId, sessionToken ? readJwtClaims(sessionToken)?.userId : undefined)
}

export function trackSignUp(props: { email?: string; eventId?: string }) {
  ga('sign_up', { method: 'magic_link', ...(props.email ? { email: props.email } : {}) })
  // Durable: every sign-in handler reloads or replaces the document right
  // after this call. No identify here: the person is attached by
  // GatewayIdentitySync once the gateway returns the stable_user_id, and this
  // event merges into it then.
  phCaptureDurable('sign_up', { method: 'magic_link' })
  // The server sends the same registration over the Conversions API and the Events API under
  // this id. Without it the browser event would be counted twice, so it is not sent.
  if (props.eventId) adEvent('CompleteRegistration', { content_name: 'magic_link' }, props.eventId)
  if (GOOGLE_ADS_SIGNUP_SEND_TO) {
    ga('conversion', { send_to: GOOGLE_ADS_SIGNUP_SEND_TO })
  }
}

export function trackLogin(props: { email?: string }) {
  ga('login', { method: 'magic_link', ...props })
  phCaptureDurable('login', { method: 'magic_link' })
  fbCustom('Login', { method: 'magic_link' })
}

/**
 * `gender` and `language` are the two answers the web flow still asks for.
 * `name` and `reason` stay optional so historical event properties keep the
 * same shape for anything already querying them.
 */
export function trackOnboardingCompleted(props: { gender: string; language: string; name?: string; reason?: string }) {
  ga('onboarding_completed', props)
  ph('onboarding_completed', props)
  fbCustom('OnboardingCompleted', props)
}

export function trackOnboardingDismissed(props: { step: string }) {
  ga('onboarding_dismissed', props)
  ph('onboarding_dismissed', props)
  fbCustom('OnboardingDismissed', props)
}

export function trackPaywallViewed(props: { reason: string }) {
  ga('paywall_viewed', props)
  ph('paywall_viewed', props)
  adEvent('ViewContent', { content_name: 'paywall', content_category: props.reason })
}

/** Cancel-flow save funnel: step is 'opened' | 'switch_yearly' | 'continue_cancel' | 'update_payment' | 'dismissed'. */
export function trackCancelFlow(props: { step: string; plan?: string }) {
  ga('cancel_flow', props)
  ph('cancel_flow', props)
}

export function trackReferralShared(props: { surface: string }) {
  ga('referral_link_shared', props)
  ph('referral_link_shared', props)
}

export function trackReferralCopied(props: { surface: string }) {
  ga('referral_link_copied', props)
  ph('referral_link_copied', props)
}

export function trackCheckoutStarted(props: { plan: 'monthly' | 'yearly'; trial: boolean }) {
  const value = props.plan === 'yearly' ? 59.99 : 12.99
  ga('begin_checkout', { currency: 'USD', value, plan: props.plan, trial: props.trial })
  ph('checkout_started', { ...props, value })
}

/**
 * The pixel half of checkout start. It waits for the Stripe Checkout Session because the session
 * id is the event id the server's InitiateCheckout carries: Meta and TikTok keep one of the two.
 * Without a session id nothing is sent from the browser.
 */
export function trackCheckoutSessionCreated(props: { plan: 'monthly' | 'yearly'; sessionId?: string | null }) {
  if (!props.sessionId) return
  const value = props.plan === 'yearly' ? 59.99 : 12.99
  adEvent('InitiateCheckout', { currency: 'USD', value, content_name: `pro_${props.plan}`, num_items: 1 }, props.sessionId)
}

/**
 * Checkout return (the visitor came back from Stripe with `?checkout=success`).
 *
 * No ad or analytics purchase is reported from here. The Stripe webhook
 * reports it once the entitlement is written, with the amount Stripe actually
 * charged: GA4 `purchase` over the Measurement Protocol, Meta `Purchase` (and
 * `StartTrial`) over the Conversions API, and the Google Ads purchase as an
 * offline click conversion for ad-attributed buyers. The browser copies of
 * those counted every purchase twice, under a different event id (Meta) or a
 * different conversion action (Ads), and at list price even for a $0 trial or
 * a discounted checkout. What stays here are non-purchase "checkout returned"
 * markers for the funnels, plus PostHog's `purchase`, which has no server-side
 * event of its own.
 *
 * Pass `transactionId` (the Stripe checkout session id) whenever it's
 * available so the markers can be joined to the server's purchase.
 */
export function trackCheckoutReturned(props: { plan: 'monthly' | 'yearly'; trial: boolean; transactionId?: string }) {
  const value = props.plan === 'yearly' ? 59.99 : 12.99
  const transactionId = props.transactionId || newEventId()
  ga('checkout_returned', { plan: props.plan, trial: props.trial, transaction_id: transactionId })
  ph('purchase', { plan: props.plan, trial: props.trial, value, transaction_id: transactionId })
  fbCustom('CheckoutReturned', { content_name: `pro_${props.plan}`, trial: props.trial })
}

/**
 * The Learn waitlist join. Same event name and shape as the landing site's
 * `email_captured`, so the "Landing conversion" action and the funnel
 * dashboard in PostHog pick it up from either host without a second query.
 */
export function trackEmailCaptured(props: { source: string }) {
  ga('email_captured', props)
  ph('email_captured', props)
  adEvent('Lead', { content_name: props.source, content_category: 'email_capture' })
}

export function trackEmailCaptureFailed(props: { source: string; error: string }) {
  ga('email_capture_failed', props)
  ph('email_capture_failed', props)
}

const STORE_HOSTS: Record<string, 'ios' | 'android' | 'chrome'> = {
  'apps.apple.com': 'ios',
  'play.google.com': 'android',
  'chromewebstore.google.com': 'chrome',
}

/**
 * One capture-phase listener for every link that leaves the app. Autocapture
 * is off in this app (see lib/posthog.ts), so without this a click on the
 * Instagram, blog, privacy, news or Chrome-store link was never recorded
 * anywhere. Every outbound anchor produces one `outbound_link_clicked` with
 * the destination host and path, the link text, and `kind`:
 *
 *   store    apps.apple.com / play.google.com / chromewebstore.google.com
 *   baba     any other *.itsbaba.com host (www, news, x)
 *   external everything else
 *
 * Store links keep their explicit `download_link_clicked` from
 * trackAppStoreClick(); this event is the "which links get clicked" view,
 * not the install conversion. Mount once from AppShell. Returns a teardown.
 */
export function installOutboundLinkTracking(): () => void {
  if (typeof document === 'undefined') return () => {}
  const onClick = (event: MouseEvent) => {
    const link = (event.target as Element | null)?.closest?.('a[href]')
    if (!(link instanceof HTMLAnchorElement)) return
    let url: URL
    try {
      url = new URL(link.href, window.location.href)
    } catch {
      return
    }
    if (url.origin === window.location.origin) return
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return
    const store = STORE_HOSTS[url.hostname]
    const kind = store ? 'store' : /(^|\.)itsbaba\.com$/.test(url.hostname) ? 'baba' : 'external'
    const text = (link.getAttribute('aria-label') || link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80)
    const props: Record<string, unknown> = {
      kind,
      host: url.hostname,
      path: url.pathname,
      text,
      page: window.location.pathname,
      source: link.dataset.source || url.searchParams.get('utm_medium') || undefined,
    }
    if (store) props.platform = store
    ga('outbound_link_clicked', props)
    ph('outbound_link_clicked', props)
  }
  document.addEventListener('click', onClick, true)
  return () => document.removeEventListener('click', onClick, true)
}

export function trackAppStoreClick(platform: 'ios' | 'android' | 'chrome', source: string) {
  const event =
    platform === 'ios' ? 'ios_app_store_click' : platform === 'android' ? 'android_play_store_click' : 'chrome_web_store_click'
  ga(event, { source })
  ph(event, { source })
  ga('download_link_clicked', { platform, source })
  ph('download_link_clicked', { platform, source })
  fbCustom('DownloadLinkClicked', { platform, source })
}

let lastTranslationTrack = 0
export function trackTranslationRun(props: { mode: string; source_lang: string; tab: string }) {
  const now = Date.now()
  if (now - lastTranslationTrack < 10_000) return
  lastTranslationTrack = now
  ga('translation_run', props)
  ph('translation_run', props)
  fbCustom('TranslationRun', props)
}

const FIRST_TRANSLATION_KEY = 'baba.app.first_translation_tracked'

/**
 * North Star activation event: the very first successful translation in this
 * browser, split by anonymous vs signed-in. Fires at most once per browser —
 * the localStorage flag is set before emitting so a later failure can't
 * double-fire, and repeat calls are no-ops.
 */
export function trackFirstTranslation(props: { signedIn: boolean }) {
  try {
    if (localStorage.getItem(FIRST_TRANSLATION_KEY)) return
    localStorage.setItem(FIRST_TRANSLATION_KEY, '1')
  } catch {}
  const params = { signed_in: props.signedIn }
  ga('first_translation', params)
  ph('first_translation', params)
  fbCustom('FirstTranslation', params)
}
